<template>
  <div class="excel-pivot-page">
    <el-card shadow="hover" class="panel-card">
      <template #header>
        <div class="panel-header">
          <div class="title-wrap">
            <span>Excel 行列提取转换</span>
            <span ref="guideBtnRef" class="guide-btn-anchor">
              <el-button link type="primary" @click="openUsageGuide">使用说明</el-button>
            </span>
          </div>
          <span class="panel-tip">支持粘贴表格 / 上传 csv、xlsx，快速转成可复制到 Excel 的结构</span>
        </div>
      </template>

      <div ref="uploadAreaRef" class="input-row">
        <el-upload
          :auto-upload="false"
          :show-file-list="false"
          accept=".csv,.xlsx,.xls,.txt"
          :on-change="handleFileChange"
        >
          <el-button type="primary">上传文件（csv/xlsx）</el-button>
        </el-upload>
        <el-button plain @click="loadSampleData">加载示例</el-button>
        <el-button plain type="danger" @click="clearAll">清空</el-button>
      </div>

      <div ref="pasteAreaRef">
        <el-input
          v-model="pasteText"
          type="textarea"
          :rows="8"
          placeholder="可直接粘贴 Excel 内容（含表头）..."
        />
      </div>
      <div class="input-row">
        <el-button type="success" @click="parsePastedText">解析粘贴内容</el-button>
        <span class="panel-tip">当前原始数据：{{ rawRows.length }} 行，{{ columns.length }} 列</span>
      </div>
    </el-card>

    <el-card shadow="hover" class="panel-card" v-loading="transforming">
      <template #header>
        <div class="panel-header">
          <span>转换配置</span>
          <span class="panel-tip">默认以日期列作表头，其余列按维度/指标组合输出</span>
        </div>
      </template>

      <div ref="configAreaRef" class="config-grid">
        <div class="config-item">
          <span class="label">表头列（默认日期）</span>
          <el-select v-model="config.headerColumn" filterable>
            <el-option v-for="col in columns" :key="col" :label="col" :value="col" />
          </el-select>
        </div>
        <div class="config-item">
          <span class="label">行维度列（可多选）</span>
          <el-select v-model="config.rowGroupColumns" multiple filterable collapse-tags collapse-tags-tooltip>
            <el-option v-for="col in availableDimensionColumns" :key="col" :label="col" :value="col" />
          </el-select>
        </div>
        <div class="config-item">
          <span class="label">指标列（作为二级表头）</span>
          <el-select v-model="config.metricColumns" multiple filterable collapse-tags collapse-tags-tooltip>
            <el-option v-for="col in availableMetricColumns" :key="col" :label="col" :value="col" />
          </el-select>
        </div>
        <template v-if="config.metricColumns.length">
          <div class="config-item" v-for="metric in config.metricColumns" :key="`alias_${metric}`">
            <span class="label">二级表头名称（{{ metric }}）</span>
            <el-input
              :model-value="metricDisplayName(metric)"
              placeholder="默认使用原字段名"
              @update:model-value="(value) => setMetricAlias(metric, value)"
            />
          </div>
        </template>
        <div class="config-item" v-if="!isSecondaryHeaderConfigMode">
          <span class="label">第一列显示名称</span>
          <el-input v-model="config.firstColumnLabel" placeholder="如：indexname / 维度" />
        </div>
        <template v-if="isSecondaryHeaderConfigMode">
          <div class="config-item">
            <span class="label">二级表头第一列（上）</span>
            <el-input v-model="config.secondHeaderTopLabel" placeholder="默认：第一列显示名称" />
          </div>
          <div class="config-item">
            <span class="label">二级表头第一列（下）</span>
            <el-input v-model="config.secondHeaderBottomLabel" placeholder="默认：行维度列名" />
          </div>
        </template>
      </div>

      <div class="input-row">
        <el-switch v-model="config.enableSecondaryHeader" active-text="启用二级表头" />
        <el-switch v-model="config.sortHeaderAsc" active-text="表头值升序" />
        <el-switch v-model="config.fillMissingDateHeaders" active-text="日期缺失自动补0（按天）" />
      </div>
    </el-card>

    <div ref="previewAreaRef">
      <el-card shadow="hover" class="panel-card" v-loading="transforming">
        <template #header>
          <div class="panel-header">
            <span>结果预览</span>
            <div ref="actionsAreaRef" class="preview-actions">
              <el-button :disabled="!columns.length" @click="swapRowAndMetricColumns">行列转换</el-button>
              <el-button type="primary" :disabled="!resultAoa.length" @click="copyResultToClipboard">复制结果（TSV）</el-button>
              <el-button type="success" :disabled="!resultAoa.length" @click="exportResultExcel">导出 Excel</el-button>
            </div>
          </div>
        </template>

        <div class="panel-tip">
          结果总行数：{{ Math.max(resultAoa.length - 1, 0) }}，总列数：{{ resultAoa[0]?.length || 0 }}；
          为了性能，表格仅预览前 {{ previewRowLimit }} 行。
        </div>

        <el-empty v-if="!previewRows.length && !transforming" description="暂无可预览数据" />
        <div v-else class="table-scroll-wrap">
          <div class="preview-table-box">
            <table class="preview-matrix">
              <thead>
                <tr v-if="isSecondaryHeaderMode">
                  <th class="first-col">{{ secondHeaderTopLabelUsed }}</th>
                  <th v-for="header in headerValuesUsed" :key="`h1_${header}`" :colspan="metricColumnsUsed.length">
                    {{ header }}
                  </th>
                </tr>
                <tr v-if="isSecondaryHeaderMode">
                  <th class="first-col">{{ secondHeaderBottomLabelUsed }}</th>
                  <template v-for="header in headerValuesUsed" :key="`h2_${header}`">
                    <th v-for="metric in metricColumnsUsed" :key="`h2_${header}_${metric}`">
                      {{ metricDisplayName(metric) }}
                    </th>
                  </template>
                </tr>
                <tr v-else>
                  <th class="first-col">{{ config.firstColumnLabel || '维度' }}</th>
                  <th v-for="header in headerValuesUsed" :key="`h0_${header}`">{{ header }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in previewRows" :key="row.__rowTitle">
                  <td class="first-col">{{ row.__rowTitle }}</td>
                  <template v-if="isSecondaryHeaderMode">
                    <template v-for="header in headerValuesUsed" :key="`b1_${row.__rowTitle}_${header}`">
                      <td v-for="metric in metricColumnsUsed" :key="`b1_${row.__rowTitle}_${header}_${metric}`">
                        {{ row[cellProp(header, metric)] ?? 0 }}
                      </td>
                    </template>
                  </template>
                  <template v-else>
                    <td v-for="header in headerValuesUsed" :key="`b0_${row.__rowTitle}_${header}`">
                      {{ row[cellProp(header, primaryMetricColumn)] ?? 0 }}
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </el-card>
    </div>

    <el-tour
      v-model="usageGuideVisible"
      v-model:current="usageGuideStep"
      :close-on-press-escape="true"
      :show-arrow="true"
      :mask="true"
      @close="resetUsageGuide"
    >
      <el-tour-step
        :target="guideBtnRef"
        title="打开使用引导"
        description="点击这里可随时重新查看步骤引导。按 Esc 或右上角关闭可中途退出。"
      />
      <el-tour-step
        :target="uploadAreaRef"
        title="导入数据"
        description="支持上传 csv/xlsx/xls，或加载示例。"
      />
      <el-tour-step
        :target="pasteAreaRef"
        title="粘贴数据"
        description="可直接粘贴 Excel 表格内容（带表头），点击“解析粘贴内容”。"
      />
      <el-tour-step
        :target="configAreaRef"
        title="设置转换规则"
        description="选择表头列（常用日期）、行维度列、指标列，支持二级表头。"
      />
      <el-tour-step
        :target="previewAreaRef"
        title="预览结果"
        description="这里会展示转换后的结构化结果，便于核对。"
      />
      <el-tour-step
        :target="actionsAreaRef"
        title="复制与导出"
        description="可一键复制 TSV 到 Excel，或导出为 Excel 文件。"
      />
    </el-tour>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import * as XLSX from 'xlsx'

const rawRows = ref([])
const columns = ref([])
const pasteText = ref('')
const transforming = ref(false)
const previewRowLimit = 300
let transformTimer = null
const usageGuideVisible = ref(false)
const usageGuideStep = ref(0)
const guideBtnRef = ref(null)
const uploadAreaRef = ref(null)
const pasteAreaRef = ref(null)
const configAreaRef = ref(null)
const previewAreaRef = ref(null)
const actionsAreaRef = ref(null)
let lockedScrollTop = 0

function setTourScrollLock(lock) {
  const body = document.body
  if (lock) {
    if (body.style.position === 'fixed') return
    lockedScrollTop = window.scrollY || window.pageYOffset || 0
    body.style.position = 'fixed'
    body.style.top = `-${lockedScrollTop}px`
    body.style.left = '0'
    body.style.right = '0'
    body.style.width = '100%'
    body.style.overflowY = 'scroll'
    return
  }
  if (body.style.position !== 'fixed') return
  body.style.position = ''
  body.style.top = ''
  body.style.left = ''
  body.style.right = ''
  body.style.width = ''
  body.style.overflowY = ''
  window.scrollTo(0, lockedScrollTop)
}

const config = reactive({
  headerColumn: '',
  rowGroupColumns: [],
  metricColumns: [],
  metricAliasMap: {},
  firstColumnLabel: '维度',
  secondHeaderTopLabel: '',
  secondHeaderBottomLabel: '',
  enableSecondaryHeader: true,
  sortHeaderAsc: true,
  fillMissingDateHeaders: true
})

const headerValuesUsed = ref([])
const metricColumnsUsed = ref([])
const previewRows = ref([])
const resultAoa = ref([])

const availableDimensionColumns = computed(() => {
  return columns.value.filter((col) => col !== config.headerColumn)
})

const availableMetricColumns = computed(() => {
  return columns.value.filter((col) => col !== config.headerColumn)
})

const isSecondaryHeaderConfigMode = computed(() => {
  return Boolean(config.enableSecondaryHeader && config.metricColumns.length > 1)
})

const isSecondaryHeaderMode = computed(() => {
  return Boolean(config.enableSecondaryHeader && metricColumnsUsed.value.length > 1)
})

const primaryMetricColumn = computed(() => {
  return metricColumnsUsed.value[0] || config.metricColumns[0] || ''
})

const secondHeaderTopLabelUsed = computed(() => {
  return config.secondHeaderTopLabel || config.firstColumnLabel || '维度'
})

const secondHeaderBottomLabelUsed = computed(() => {
  return config.secondHeaderBottomLabel || config.rowGroupColumns.join('/') || '维度'
})

const metricAliasSignature = computed(() => {
  return config.metricColumns.map((metric) => `${metric}:${config.metricAliasMap[metric] ?? ''}`).join('|')
})

function openUsageGuide() {
  usageGuideVisible.value = true
  usageGuideStep.value = 0
}

function resetUsageGuide() {
  usageGuideStep.value = 0
  setTourScrollLock(false)
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return 0
  const cleaned = String(value).replace(/,/g, '')
  const num = Number(cleaned)
  return Number.isFinite(num) ? num : 0
}

function isNumericLike(value) {
  if (value === null || value === undefined || value === '') return false
  const text = String(value).trim().replace(/,/g, '')
  if (!text) return false
  return /^-?\d+(\.\d+)?$/.test(text)
}

function getColumnNumericRatio(rows, col, sampleLimit = 200) {
  if (!rows?.length) return 0
  const limit = Math.min(rows.length, sampleLimit)
  let nonEmpty = 0
  let numeric = 0
  for (let i = 0; i < limit; i += 1) {
    const value = rows[i]?.[col]
    if (value === null || value === undefined || String(value).trim() === '') continue
    nonEmpty += 1
    if (isNumericLike(value)) numeric += 1
  }
  if (!nonEmpty) return 0
  return numeric / nonEmpty
}

function mergeMetricValue(prev, nextRaw) {
  const nextText = String(nextRaw ?? '').trim()
  if (!nextText) return prev

  const nextIsNum = isNumericLike(nextText)
  if (prev === undefined || prev === null || prev === '') {
    return nextIsNum ? toNumber(nextText) : nextText
  }

  const prevIsNum = typeof prev === 'number' || isNumericLike(prev)
  if (prevIsNum && nextIsNum) {
    return toNumber(prev) + toNumber(nextText)
  }

  const list = []
  const seen = new Set()
  ;[String(prev), nextText].forEach((item) => {
    const text = String(item || '').trim()
    if (!text) return
    if (!seen.has(text)) {
      seen.add(text)
      list.push(text)
    }
  })
  return list.join('、')
}

function normalizeDateText(value) {
  const text = String(value ?? '').trim()
  if (!text) return text

  const datePart = text.match(/^(\d{4}[/-]\d{1,2}[/-]\d{1,2})/)?.[1] || text

  // yyyy-mm-dd / yyyy/m/d
  let match = datePart.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/)
  if (match) {
    const y = Number(match[1])
    const m = Number(match[2])
    const d = Number(match[3])
    if (y >= 1900 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    }
  }

  // m/d/yy or m/d/yyyy
  match = datePart.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/)
  if (match) {
    const m = Number(match[1])
    const d = Number(match[2])
    let y = Number(match[3])
    if (y < 100) y += 2000
    if (y >= 1900 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    }
  }

  return text
}

function isDateLikeColumnName(columnName) {
  const col = String(columnName || '').toLowerCase()
  return ['time_scope', 'date', 'day', 'dt', '日期', '时间'].some((k) => col.includes(k))
}

function toDateObjectFromYmd(value) {
  const text = String(value ?? '').trim()
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  const y = Number(match[1])
  const m = Number(match[2])
  const d = Number(match[3])
  const date = new Date(y, m - 1, d)
  if (Number.isNaN(date.getTime())) return null
  return date
}

function formatDateYmd(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function buildContinuousDateHeaders(values, asc = true) {
  if (!values.length) return []
  const normalized = values.map((v) => normalizeDateText(v))
  const dates = normalized.map((v) => toDateObjectFromYmd(v))
  if (dates.some((d) => !d)) return normalized

  const sorted = [...dates].sort((a, b) => a.getTime() - b.getTime())
  const start = new Date(sorted[0])
  const end = new Date(sorted[sorted.length - 1])
  const list = []
  const cursor = new Date(start)
  while (cursor.getTime() <= end.getTime()) {
    list.push(formatDateYmd(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return asc ? list : list.reverse()
}

function normalizeDimensionValueByColumn(columnName, value) {
  if (!isDateLikeColumnName(columnName)) return String(value ?? '').trim() || '-'
  const normalized = normalizeDateText(value)
  return normalized || '-'
}

function normalizeHeader(header, index, used) {
  const base = String(header || '').trim() || `col_${index + 1}`
  if (!used[base]) {
    used[base] = 1
    return base
  }
  used[base] += 1
  return `${base}_${used[base]}`
}

function aoaToRows(aoa) {
  if (!Array.isArray(aoa) || aoa.length < 2) return { rows: [], headers: [] }
  const headerRaw = aoa[0] || []
  const used = {}
  const headers = headerRaw.map((h, idx) => normalizeHeader(h, idx, used))
  const rows = aoa
    .slice(1)
    .filter((row) => Array.isArray(row) && row.some((cell) => String(cell ?? '').trim() !== ''))
    .map((row) => {
      const obj = {}
      headers.forEach((h, idx) => {
        obj[h] = row[idx] ?? ''
      })
      return obj
    })
  return { rows, headers }
}

function detectDateLikeColumn(cols) {
  const found = cols.find((col) => isDateLikeColumnName(col))
  return found || cols[0] || ''
}

function guessDefaultMetrics(cols, headerCol, rows = []) {
  const keyWords = ['pv', 'uv', 'timeonsite', 'amount', 'cnt', 'count', 'num', 'rate', '订单', '人数', '金额', '商品数', '件数', '次数']
  return cols.filter((col) => {
    if (col === headerCol) return false
    const nameHit = keyWords.some((k) => col.toLowerCase().includes(String(k).toLowerCase()))
    const numericRatio = getColumnNumericRatio(rows, col)
    return nameHit || numericRatio >= 0.8
  })
}

function applyRawRows(rows, headers) {
  rawRows.value = rows
  columns.value = headers
  config.headerColumn = detectDateLikeColumn(headers)

  const defaultDims = headers.filter((c) => c !== config.headerColumn && !['pv', 'uv', 'timeonsite'].includes(c.toLowerCase()))
  config.rowGroupColumns = defaultDims.length ? [defaultDims[0]] : headers.filter((c) => c !== config.headerColumn).slice(0, 1)

  const metrics = guessDefaultMetrics(headers, config.headerColumn, rows)
  const fallbackNumeric = headers
    .filter((c) => c !== config.headerColumn && c !== config.rowGroupColumns[0])
    .filter((c) => getColumnNumericRatio(rows, c) >= 0.8)
  config.metricColumns = metrics.length
    ? metrics
    : (fallbackNumeric.length ? fallbackNumeric.slice(0, 2) : headers.filter((c) => c !== config.headerColumn && c !== config.rowGroupColumns[0]).slice(0, 2))
  config.metricAliasMap = {}
  config.metricColumns.forEach((metric) => {
    config.metricAliasMap[metric] = metric
  })
  config.firstColumnLabel = config.rowGroupColumns.join('/') || '维度'
  config.secondHeaderTopLabel = config.firstColumnLabel
  config.secondHeaderBottomLabel = config.rowGroupColumns.join('/') || '维度'
}

function parsePastedText() {
  const text = pasteText.value.trim()
  if (!text) {
    ElMessage.warning('请先粘贴内容')
    return
  }

  try {
    if (text.includes('\t')) {
      const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '')
      const aoa = lines.map((line) => line.split('\t'))
      const { rows, headers } = aoaToRows(aoa)
      applyRawRows(rows, headers)
      ElMessage.success(`解析成功：${rows.length} 行`)
      return
    }

    const workbook = XLSX.read(text, { type: 'string' })
    const sheet = workbook.Sheets[workbook.SheetNames[0]]
    const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' })
    const { rows, headers } = aoaToRows(aoa)
    applyRawRows(rows, headers)
    ElMessage.success(`解析成功：${rows.length} 行`)
  } catch (error) {
    console.error(error)
    ElMessage.error('解析粘贴内容失败，请确认格式')
  }
}

function scoreDecodedText(text) {
  if (!text) return -9999
  let score = 0
  const mojibakeMatches = text.match(/[ÃÂÐÑçæåäöüøðþ]/g) || []
  score -= mojibakeMatches.length * 2
  const replacementMatches = text.match(/�/g) || []
  score -= replacementMatches.length * 5
  const cjkMatches = text.match(/[\u4e00-\u9fff]/g) || []
  score += cjkMatches.length
  const lineBreaks = text.match(/\r?\n/g) || []
  score += lineBreaks.length
  return score
}

function decodeCsvBuffer(buffer) {
  // 1) 优先严格 UTF-8。若严格可解码，通常就是正确编码，避免被误判为 GBK 导致“鐞冩槦...”。
  try {
    const utf8Strict = new TextDecoder('utf-8', { fatal: true }).decode(buffer)
    if (utf8Strict) return utf8Strict
  } catch (error) {
    // UTF-8 严格失败才走中文编码兜底
    console.warn('utf-8 strict decode failed, fallback to gbk/gb18030', error)
  }

  // 2) UTF-8 严格失败后，再在 gbk / gb18030 中择优。
  const fallbackEncodings = ['gb18030', 'gbk', 'utf-8']
  let bestText = ''
  let bestScore = -Infinity
  fallbackEncodings.forEach((encoding) => {
    try {
      const text = new TextDecoder(encoding, { fatal: false }).decode(buffer)
      const score = scoreDecodedText(text)
      if (score > bestScore) {
        bestScore = score
        bestText = text
      }
    } catch (error) {
      console.warn(`decode with ${encoding} failed`, error)
    }
  })
  return bestText
}

function handleFileChange(fileInfo) {
  const rawFile = fileInfo?.raw
  if (!rawFile) return
  const isCsvLike = /\.(csv|txt)$/i.test(rawFile.name || '')
  const reader = new FileReader()
  reader.onload = (e) => {
    try {
      const data = e.target?.result
      let workbook
      if (isCsvLike) {
        const decoded = decodeCsvBuffer(data)
        workbook = XLSX.read(decoded, { type: 'string', raw: false })
      } else {
        workbook = XLSX.read(data, { type: 'array' })
      }
      const sheet = workbook.Sheets[workbook.SheetNames[0]]
      const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' })
      const { rows, headers } = aoaToRows(aoa)
      applyRawRows(rows, headers)
      ElMessage.success(`文件读取成功：${rows.length} 行`)
    } catch (error) {
      console.error(error)
      ElMessage.error('文件解析失败，请检查文件格式')
    }
  }
  reader.readAsArrayBuffer(rawFile)
}

function clearAll() {
  rawRows.value = []
  columns.value = []
  pasteText.value = ''
  previewRows.value = []
  resultAoa.value = []
  config.headerColumn = ''
  config.rowGroupColumns = []
  config.metricColumns = []
  config.metricAliasMap = {}
  config.firstColumnLabel = '维度'
  config.secondHeaderTopLabel = ''
  config.secondHeaderBottomLabel = ''
  config.fillMissingDateHeaders = true
}

function loadSampleData() {
  const aoa = [
    ['indexname', 'time_scope', 'pv', 'uv'],
    ['visit', '2026-03-03', '1024', '501'],
    ['visit', '2026-03-04', '1201', '630'],
    ['addShop', '2026-03-03', '380', '210'],
    ['addShop', '2026-03-04', '420', '268']
  ]
  const { rows, headers } = aoaToRows(aoa)
  applyRawRows(rows, headers)
  ElMessage.success('示例数据已加载')
}

function cellProp(header, metric) {
  return `${header}__${metric}`
}

function ensureMetricAliasDefaults() {
  config.metricColumns.forEach((metric) => {
    if (!(metric in config.metricAliasMap)) {
      config.metricAliasMap[metric] = metric
    }
  })
}

function metricDisplayName(metric) {
  const alias = config.metricAliasMap[metric]
  return String(alias ?? '').trim() || metric
}

function setMetricAlias(metric, value) {
  config.metricAliasMap[metric] = value
}

function swapRowAndMetricColumns() {
  if (!columns.value.length) return
  if (!config.headerColumn || !config.rowGroupColumns.length) {
    ElMessage.warning('请先选择表头列和行维度列后再转换')
    return
  }

  const oldHeaderColumn = config.headerColumn
  const oldRowGroupColumns = [...config.rowGroupColumns]
  const nextHeaderColumn = oldRowGroupColumns[0]

  const nextRowGroupColumns = [oldHeaderColumn, ...oldRowGroupColumns.slice(1)]
    .filter((col) => col && col !== nextHeaderColumn)
    .filter((col, index, arr) => arr.indexOf(col) === index)

  config.headerColumn = nextHeaderColumn
  config.rowGroupColumns = nextRowGroupColumns
  config.firstColumnLabel = config.rowGroupColumns.join('/') || '维度'

  if (!config.secondHeaderTopLabel) {
    config.secondHeaderTopLabel = config.firstColumnLabel || '维度'
  }
  config.secondHeaderBottomLabel = config.rowGroupColumns.join('/') || '维度'

  ElMessage.success('已交换表头列与行维度列（行维度第1列作为新表头）')
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function buildTransformResult() {
  if (!rawRows.value.length || !config.headerColumn || !config.rowGroupColumns.length || !config.metricColumns.length) {
    headerValuesUsed.value = []
    metricColumnsUsed.value = []
    previewRows.value = []
    resultAoa.value = []
    return
  }

  const headerSet = new Set()
  const rowMap = new Map()
  const metricNumericMap = {}
  config.metricColumns.forEach((metric) => {
    metricNumericMap[metric] = getColumnNumericRatio(rawRows.value, metric) >= 0.8
  })

  rawRows.value.forEach((row) => {
    const headerVal = normalizeDateText(row[config.headerColumn] ?? '')
    if (!headerVal) return
    headerSet.add(headerVal)

    const rowKey = config.rowGroupColumns
      .map((col) => normalizeDimensionValueByColumn(col, row[col]))
      .join(' / ')
    if (!rowMap.has(rowKey)) rowMap.set(rowKey, {})
    const bucket = rowMap.get(rowKey)
    config.metricColumns.forEach((metric) => {
      const key = cellProp(headerVal, metric)
      bucket[key] = mergeMetricValue(bucket[key], row[metric])
    })
  })

  let headers = Array.from(headerSet)
  if (config.fillMissingDateHeaders && isDateLikeColumnName(config.headerColumn)) {
    headers = buildContinuousDateHeaders(headers, config.sortHeaderAsc)
  } else if (config.sortHeaderAsc) {
    headers.sort((a, b) => a.localeCompare(b, 'zh-CN'))
  } else {
    headers.sort((a, b) => b.localeCompare(a, 'zh-CN'))
  }
  headerValuesUsed.value = headers
  metricColumnsUsed.value = [...config.metricColumns]

  const transformed = []
  const aoa = []
  ensureMetricAliasDefaults()
  if (isSecondaryHeaderMode.value) {
    const head1 = [config.secondHeaderTopLabel || config.firstColumnLabel || '维度']
    headers.forEach((h) => {
      config.metricColumns.forEach(() => head1.push(h))
    })
    const head2 = [config.secondHeaderBottomLabel || config.rowGroupColumns.join('/') || '维度']
    headers.forEach(() => {
      config.metricColumns.forEach((m) => head2.push(metricDisplayName(m)))
    })
    aoa.push(head1, head2)
  } else {
    aoa.push([config.firstColumnLabel || '维度', ...headers])
  }

  for (const [rowKey, bucket] of rowMap.entries()) {
    const rowObj = { __rowTitle: rowKey }
    const aoaRow = [rowKey]
    if (isSecondaryHeaderMode.value) {
      headers.forEach((h) => {
        config.metricColumns.forEach((m) => {
          const key = cellProp(h, m)
          const value = bucket[key] ?? (metricNumericMap[m] ? 0 : '')
          rowObj[key] = value
          aoaRow.push(value)
        })
      })
    } else {
      const metric = primaryMetricColumn.value
      headers.forEach((h) => {
        const key = cellProp(h, metric)
        const value = bucket[key] ?? (metricNumericMap[metric] ? 0 : '')
        rowObj[key] = value
        aoaRow.push(value)
      })
    }
    transformed.push(rowObj)
    aoa.push(aoaRow)
  }

  previewRows.value = transformed.slice(0, previewRowLimit)
  resultAoa.value = aoa
}

function scheduleTransform() {
  if (transformTimer) clearTimeout(transformTimer)
  transforming.value = true
  transformTimer = setTimeout(() => {
    buildTransformResult()
    transforming.value = false
  }, 80)
}

watch(
  () => [
    rawRows.value.length,
    config.headerColumn,
    config.rowGroupColumns.join('|'),
    config.metricColumns.join('|'),
    metricAliasSignature.value,
    config.firstColumnLabel,
    config.secondHeaderTopLabel,
    config.secondHeaderBottomLabel,
    config.enableSecondaryHeader,
    config.sortHeaderAsc,
    config.fillMissingDateHeaders
  ],
  () => {
    scheduleTransform()
  }
)

watch(
  () => usageGuideVisible.value,
  (visible) => {
    setTourScrollLock(visible)
  }
)

onBeforeUnmount(() => {
  setTourScrollLock(false)
})

onMounted(() => {
  // 兜底恢复，避免极端情况下上次引导状态残留
  setTourScrollLock(false)
})

async function copyResultToClipboard() {
  if (!resultAoa.value.length) return
  const text = resultAoa.value.map((row) => row.map((v) => String(v ?? '')).join('\t')).join('\n')
  try {
    await navigator.clipboard.writeText(text)
    ElMessage.success('已复制，可直接粘贴到 Excel')
  } catch (error) {
    console.error(error)
    ElMessage.error('复制失败，请检查浏览器权限')
  }
}

function exportResultExcel() {
  if (!resultAoa.value.length) return
  const styleCell = 'border:1px solid #d9d9d9;text-align:center;vertical-align:middle;padding:6px 10px;white-space:nowrap;'
  let html = '<html><head><meta charset="UTF-8"></head><body>'
  html += '<table border="1" cellspacing="0" cellpadding="0" style="border-collapse:collapse;">'

  if (isSecondaryHeaderMode.value && metricColumnsUsed.value.length > 1) {
    const metricCount = metricColumnsUsed.value.length
    html += '<thead>'
    html += '<tr>'
    html += `<th style="${styleCell}">${escapeHtml(secondHeaderTopLabelUsed.value)}</th>`
    headerValuesUsed.value.forEach((header) => {
      html += `<th style="${styleCell}" colspan="${metricCount}">${escapeHtml(header)}</th>`
    })
    html += '</tr>'

    html += '<tr>'
    html += `<th style="${styleCell}">${escapeHtml(secondHeaderBottomLabelUsed.value)}</th>`
    headerValuesUsed.value.forEach(() => {
      metricColumnsUsed.value.forEach((metric) => {
        html += `<th style="${styleCell}">${escapeHtml(metricDisplayName(metric))}</th>`
      })
    })
    html += '</tr>'
    html += '</thead>'

    html += '<tbody>'
    for (let i = 2; i < resultAoa.value.length; i += 1) {
      const row = resultAoa.value[i] || []
      html += '<tr>'
      row.forEach((cell) => {
        html += `<td style="${styleCell}">${escapeHtml(cell)}</td>`
      })
      html += '</tr>'
    }
    html += '</tbody>'
  } else {
    html += '<thead><tr>'
    const headRow = resultAoa.value[0] || []
    headRow.forEach((cell) => {
      html += `<th style="${styleCell}">${escapeHtml(cell)}</th>`
    })
    html += '</tr></thead>'

    html += '<tbody>'
    for (let i = 1; i < resultAoa.value.length; i += 1) {
      const row = resultAoa.value[i] || []
      html += '<tr>'
      row.forEach((cell) => {
        html += `<td style="${styleCell}">${escapeHtml(cell)}</td>`
      })
      html += '</tr>'
    }
    html += '</tbody>'
  }

  html += '</table></body></html>'

  const now = new Date()
  const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`
  const blob = new Blob([`\ufeff${html}`], { type: 'application/vnd.ms-excel;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `excel_行列提取转换_${stamp}.xls`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
}
</script>

<style scoped>
.excel-pivot-page {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
  min-width: 0;
  overflow-x: hidden;
}

.excel-pivot-page > * {
  min-width: 0;
}

.panel-card {
  border-radius: 12px;
  min-width: 0;
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.title-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
}

.guide-btn-anchor {
  display: inline-flex;
  align-items: center;
}

.panel-tip {
  font-size: 12px;
  color: #6b7280;
  word-break: break-word;
}

.input-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 12px 0;
  flex-wrap: wrap;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(280px, 1fr));
  gap: 12px;
}

.config-item {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.config-item .label {
  font-size: 13px;
  color: #374151;
}

.preview-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.table-scroll-wrap {
  display: block;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.excel-pivot-page :deep(.el-card__body) {
  min-width: 0;
  overflow-x: hidden;
}

.table-scroll-wrap :deep(.el-table__inner-wrapper) {
  overflow-x: auto;
}

.table-scroll-wrap :deep(.el-table) {
  width: 100%;
}

.config-item :deep(.el-select),
.config-item :deep(.el-input),
.config-item :deep(.el-textarea) {
  width: 100%;
  max-width: 100%;
}

.preview-table-box {
  min-width: max-content;
}

.preview-matrix {
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;
  font-size: 13px;
}

.preview-matrix th,
.preview-matrix td {
  border: 1px solid #e5e7eb;
  padding: 8px 10px;
  text-align: center;
  white-space: nowrap;
  background: #fff;
}

.preview-matrix thead th {
  background: #f8fafc;
  font-weight: 600;
}

.preview-matrix .first-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #f8fafc;
  min-width: 180px;
}

.preview-matrix tbody .first-col {
  background: #fff;
  z-index: 1;
}

@media (max-width: 960px) {
  .config-grid {
    grid-template-columns: 1fr;
  }
}
</style>
